import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { Button, ButtonIcon, ButtonSize, ButtonVariant, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import React, { useCallback } from "react";

import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import { EXTERNAL_ROUTES_FOR_APPLICATION, ROUTES } from "@/constants";

import type { ContextCapPopoverProps } from "./types";

const ContextCapPopover = ({
  isOpen,
  hasUserSubscribedPlan,
  hideOnClose,
  onClose,
  onStopPropagation,
  onToggleHideOnClose,
  isCutoffMessageLoaded,
  onJumpToCutoff,
}: ContextCapPopoverProps) => {
  const applicationId = basicAppConfig.applicationId;
  const contextWindowDocUrl = EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.context_window_doc;

  const handleJumpClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onJumpToCutoff?.();
      onClose();
    },
    [onJumpToCutoff, onClose],
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/20 dark:bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="ContextCapPopover">
      <div
        className="absolute top-16 right-4 left-4 mx-auto max-w-xs bg-gray-2 dark:bg-gray-4 rounded-xl shadow-lg border border-gray-6 p-4 animate-in fade-in slide-in-from-top-2"
        onClick={onStopPropagation}
      >
        {/* Close button */}
        <ButtonIcon
          icon={IconVariants.CLOSE}
          onClick={onClose}
          variant={ButtonVariant.TEXT}
          size={ButtonSize.SMALL}
          className="absolute top-2 right-2"
          aria-label="Close"
          data-testid="ContextCapPopover-CloseButton"
        />

        <div className="flex flex-col gap-3 pr-4">
          <Text variant={TextVariants.LABEL_MEDIUM_BOLD} className="text-gray-12">
            <Trans i18nKey="chat:modal.contextLimit.title" />
          </Text>
          <Text variant={TextVariants.PARAGRAPH_SMALL_REGULAR} className="text-gray-11 leading-relaxed">
            <Trans i18nKey="chat:modal.contextLimit.description" />
          </Text>
          <LocalizedLink
            to={contextWindowDocUrl}
            target="_blank"
            className="text-sm text-link flex items-center gap-1"
            data-testid="ContextCapPopover-DocsLink"
          >
            <span>📖</span>
            <span>
              <Trans i18nKey="chat:modal.contextLimit.cta.learnMore" />
            </span>
          </LocalizedLink>
          {!!(isCutoffMessageLoaded && onJumpToCutoff) && (
            <button
              onClick={handleJumpClick}
              className="text-sm text-link flex items-center gap-1 cursor-pointer bg-transparent border-0 p-0"
              data-testid="ContextCapPopover-JumpButton"
            >
              <span>📍</span>
              <span>
                <Trans i18nKey="chat:modal.contextLimit.cta.jumpToCutoff" />
              </span>
            </button>
          )}
          {!hasUserSubscribedPlan && (
            <LocalizedLink to={ROUTES.subscribe} className="mt-1">
              <Button variant={ButtonVariant.CONTAINED} className="w-full" data-testid="ContextCapPopover-UpgradeButton">
                <Trans i18nKey="chat:modal.contextLimit.cta.upgrade" />
              </Button>
            </LocalizedLink>
          )}
          <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-11 hover:text-gray-12 transition-colors mt-1">
            <input
              type="checkbox"
              checked={hideOnClose}
              onChange={onToggleHideOnClose}
              className="w-4 h-4 rounded border-gray-8 text-purple-600 focus:ring-purple-500 focus:ring-offset-0 cursor-pointer"
              data-testid="ContextCapPopover-HideCheckbox"
            />
            <Trans i18nKey="chat:modal.contextLimit.hideIndicator" />
          </label>
        </div>
      </div>
    </div>
  );
};

export default ContextCapPopover;
